@if (Match) {
  <!-- P6:1657-1673 -->
  <h1 class="cx-h1">{{ View.Title }}</h1>
  <p class="cx-sub">What this role asks for, and how your record compares.</p>

  <!-- Score card, P6:1659-1661. P6:467-472: no striped bar, no "Up to {hi}%" sentence. -->
  <div class="cx-card cx-score">
    <div class="big">
      <b>{{ View.PercentNumber }}<small>%</small></b>
      <span class="cx-st" [class.ok]="View.Badge.Tone === 'ok'" [class.near]="View.Badge.Tone === 'near'" [class.grey]="View.Badge.Tone === 'grey'">{{ View.Badge.Label }}</span>
    </div>
    <div class="cx-bar2" role="img" [attr.aria-label]="'Match ' + View.PercentLabel"><span class="lo" [style.width.%]="View.Percent"></span></div>
    @if (View.Postings) { <p>{{ View.Postings }}</p> }
  </div>

  <!-- Why this role fits: the stored narrative, under the score card. Failing or absent, the block is simply not there. -->
  @if (NarrativeLoading) {
    <div class="cx-card cx-why" aria-busy="true">
      <h2 class="cx-h2">Why this role fits</h2>
      <div class="cx-skel" aria-hidden="true"><span></span><span></span><span class="short"></span></div>
    </div>
  } @else if (View.NarrativeText.length > 0) {
    <div class="cx-card cx-why">
      <h2 class="cx-h2">Why this role fits</h2>
      @for (para of View.NarrativeText; track $index) { <p class="cx-narr">{{ para }}</p> }
      @if (View.NarrativeLabel) { <p class="cx-narr-label">{{ View.NarrativeLabel }}</p> }
    </div>
  }

  <div class="cx-role">
    <div class="cx-rl">
      <!-- P6:1662-1667 -->
      <h2 class="cx-h2">Skills <span class="cx-skn">{{ View.SkillsHave }} of {{ View.SkillsTotal }}</span></h2>
      <p class="cx-h2s">Have one your CV didn't mention? Mark it and your score updates.</p>
      <div class="cx-card cx-skl">
        @for (row of View.Skills; track row.ProviderTagID) {
          <div class="row" [class.on]="row.Met" [attr.title]="row.Title">
            <span class="mk" aria-hidden="true">
              @if (row.Met) { <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg> }
            </span>
            <span class="nm">
              <span class="nm-t">{{ row.Name }}@if (row.Met) { <span class="cx-sr"> (met)</span> }</span>
              @if (row.Sub || row.State) { <small>{{ row.Sub }}@if (row.Sub && row.State) { · }{{ row.State }}</small> }
            </span>
            @if (row.CanAssert) {
              <button class="have" type="button" [attr.aria-label]="'I have this: ' + row.Name" (click)="IHaveThis(row.ProviderTagID)">I have this</button>
            }
          </div>
        }
      </div>

      @if (View.Credentials.length > 0) {
        <!-- P6:1668-1670 -->
        <h2 class="cx-h2 cx-h2-gap">Credentials <span class="cx-skn">{{ View.CredentialsHave }} of {{ View.Credentials.length }}</span></h2>
        @if (View.EducationLine) { <p class="cx-h2s">{{ View.EducationLine }}</p> }
        <div class="cx-card cx-skl">
          @for (row of View.Credentials; track row.ProviderTagID) {
            <div class="row" [class.on]="row.Met" [attr.title]="row.Title">
              <span class="mk" aria-hidden="true">
                @if (row.Met) { <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg> }
              </span>
              <span class="nm">
                <span class="nm-t">{{ row.Name }}@if (row.Met) { <span class="cx-sr"> (met)</span> }</span>
                @if (row.Sub || row.State) { <small>{{ row.Sub }}@if (row.Sub && row.State) { · }{{ row.State }}</small> }
              </span>
              @if (row.CanAssert) {
                <button class="have" type="button" [attr.aria-label]="'I have this: ' + row.Name" (click)="IHaveThis(row.ProviderTagID)">I have this</button>
              }
            </div>
          }
        </div>
      } @else if (View.EducationLine) {
        <p class="cx-h2s cx-h2-gap">{{ View.EducationLine }}</p>
      }
    </div>

    <!-- Openings, P6:1685-1707 (sticky right column, P6:469) -->
    <div class="cx-rr">
      <div class="cx-jobs" id="cx-openings">
        <h2 class="cx-h2">Openings</h2>
        <p class="cx-h2s">Live listings in Job Finder.</p>
        @if (ShowOpeningsSkeleton) {
          <div class="cx-card cx-jl cx-skel-rows" aria-busy="true"><span></span><span></span><span></span></div>
        } @else if (View.OpeningRows.length > 0) {
          <div class="cx-card cx-jl">
            @for (job of View.OpeningRows; track job.JobID) {
              <button class="cx-job" type="button" (click)="OpenOne(job.JobID)">
                <span class="jt">
                  <b>{{ job.Title }}@if (job.IsNew) { <span class="nw">New</span> }</b>
                  @if (job.Org) { <span class="jo">{{ job.Org }}</span> }
                </span>
                @if (job.Salary) { <span class="js"><span class="sv">{{ job.Salary }}</span>@if (job.SalaryNote) { <span class="sk">{{ job.SalaryNote }}</span> }</span> }
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
              </button>
            }
          </div>
          <button class="cx-alljobs" type="button" (click)="OpenAll()">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5"/><path d="M15.4 15.4 21 21"/></svg>
            <span>See all jobs for this path in Job Finder</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
          </button>
        } @else if (ShowEmptyOpenings) {
          <p class="cx-h2s">{{ EmptyOpeningsCopy }} <button class="cx-inline" type="button" (click)="OpenBrowse()">Browse all jobs</button>.</p>
        } @else if (ShowOpeningsUnavailable) {
          <p class="cx-h2s">{{ OpeningsUnavailableCopy }} <button class="cx-inline" type="button" (click)="OpenBrowse()">Browse all jobs</button>.</p>
        }
      </div>
    </div>
  </div>
}
